import type {
  IncidentGroupActionName,
  IncidentGroupActionRequest,
  IncidentGroupActionResponse,
} from '../../services/incidentsApi';
import { incidentsApi } from '../../services/incidentsApi';
import { humanizePatternGroupActionError } from './patternGroupActionErrors';

export type PatternGroupActionBackgroundPayload = {
  groupKey: string;
  body: IncidentGroupActionRequest;
  mode: IncidentGroupActionName;
  matchedCount: number;
  patternLabel: string;
};

function actionVerb(mode: IncidentGroupActionName, tense: 'progress' | 'done' | 'failed'): string {
  if (mode === 'close') {
    if (tense === 'progress') return 'Closing';
    if (tense === 'done') return 'Closed';
    return 'Close';
  }
  if (mode === 'assign') {
    if (tense === 'progress') return 'Assigning';
    if (tense === 'done') return 'Assigned';
    return 'Assign';
  }
  if (tense === 'progress') return 'Suppressing';
  if (tense === 'done') return 'Suppressed';
  return 'Suppress';
}

export function patternGroupActionStartedMessage(
  mode: IncidentGroupActionName,
  matchedCount: number,
  patternLabel: string,
): string {
  const n = Math.max(0, Number(matchedCount) || 0);
  const label = String(patternLabel || 'this recurring risk').trim() || 'this recurring risk';
  return `${actionVerb(mode, 'progress')} ${n.toLocaleString()} open linked record${n === 1 ? '' : 's'} for ${label} in the background. You can keep reviewing.`;
}

export function parsePatternGroupActionAffectedCount(
  result: IncidentGroupActionResponse | null | undefined,
): number | null {
  if (!result || result.affected_count == null) return null;
  const n = Number(result.affected_count);
  return Number.isFinite(n) ? Math.max(0, n) : null;
}

export function assertPatternGroupActionExecuteResponse(
  result: IncidentGroupActionResponse,
  request: IncidentGroupActionRequest,
): void {
  if (request.dry_run) return;
  if (result.dry_run) {
    throw new Error('Server returned a preview-only response for an execute request.');
  }
  if (parsePatternGroupActionAffectedCount(result) == null) {
    throw new Error('Server response missing affected_count.');
  }
}

export function patternGroupActionSuccessMessage(
  mode: IncidentGroupActionName,
  result: IncidentGroupActionResponse,
): string {
  const affected = parsePatternGroupActionAffectedCount(result);
  if (affected == null) {
    return `${actionVerb(mode, 'failed')} group action failed: server response missing affected_count.`;
  }
  const skipped = Math.max(0, Number(result?.skipped_count) || 0);
  const matched = Math.max(0, Number(result?.matched_count) || 0);
  if (affected === 0 && matched > 0) {
    let text = `No open linked records were updated (${matched.toLocaleString()} matched in scope).`;
    if (skipped > 0) {
      text += ` ${skipped.toLocaleString()} skipped.`;
    }
    return text;
  }
  let text = `${actionVerb(mode, 'done')} ${affected.toLocaleString()} open linked record${affected === 1 ? '' : 's'}.`;
  if (skipped > 0) {
    text += ` ${skipped.toLocaleString()} skipped.`;
  }
  return text;
}

export function patternGroupActionFailedMessage(
  mode: IncidentGroupActionName,
  detail: string,
): string {
  const msg = String(detail || 'Action failed').trim();
  return `${actionVerb(mode, 'failed')} group action failed: ${msg}`;
}

export async function runPatternGroupActionInBackground(
  payload: PatternGroupActionBackgroundPayload,
  handlers: {
    onSuccess: (result: IncidentGroupActionResponse) => void;
    onError: (message: string) => void;
  },
): Promise<void> {
  try {
    const result = await incidentsApi.applyIncidentGroupAction(payload.groupKey, payload.body);
    assertPatternGroupActionExecuteResponse(result, payload.body);
    handlers.onSuccess(result);
  } catch (error: any) {
    const detail =
      error?.response?.data?.detail
      || error?.response?.data?.error
      || error?.message
      || 'Action failed';
    handlers.onError(
      patternGroupActionFailedMessage(payload.mode, humanizePatternGroupActionError(String(detail))),
    );
  }
}
